import * as Msal from 'msal';
import { IAuthenticationService, IAuthenticationToken, IAuthenticationUser } from './interfaces/IAuthenticationService';

export interface IAuthenticationServiceExtended extends IAuthenticationService {
  signOut?: () => void;
}

export class AuthenticationService implements IAuthenticationServiceExtended {
  private msalObj: Msal.UserAgentApplication;
  private interactiveLoginRequest!: Msal.AuthenticationParameters;
  private interactiveTokenRequest!: Msal.AuthenticationParameters;
  private silentTokenRequest!: Msal.AuthenticationParameters;

  constructor(config: Msal.Configuration) {
    this.msalObj = new Msal.UserAgentApplication(config);
    this.setRequestObjects();
  }

  public init = async (): Promise<AuthenticationService> => {
    let accountInfo = this.msalObj.getAccount();

    if (accountInfo == null) {
      await this.login();
    }
    return this;
  };

  public signOut = () => {
    this.msalObj.logout();
  };

  public acquireToken = async (resource: string): Promise<IAuthenticationToken> => {
    const token = await this.getResourceToken(resource);

    return Promise.resolve({ token });
  };

  public isUserLoggedIn = (): boolean => {
    let accountInfo = this.msalObj.getAccount();

    return accountInfo !== null;
  };

  public getUser = async (): Promise<IAuthenticationUser> => {
    let accountInfo = this.msalObj.getAccount();

    if (accountInfo == null) {
      await this.login();
      accountInfo = this.msalObj.getAccount();
    }

    return Promise.resolve({
      profile: {
        oid: accountInfo?.idToken['oid'],
        tid: accountInfo.idToken['tid'],
        upn: accountInfo.idToken['upn'] || accountInfo.userName,
      },
    });
  };

  public notifySuccess = async (data: string): Promise<void> => {
    alert(`AuthenticationService: Data: ${data}`);
  };

  public notifyFailure = async (reason: string): Promise<void> => {
    alert(`AuthenticationService: Reason: ${reason}`);
  };

  /**
   * Initialize request objects used by this AuthModule.
   */
  private setRequestObjects(): void {
    this.interactiveLoginRequest = {
      scopes: [],
      prompt: 'login',
    };

    this.interactiveTokenRequest = {
      scopes: ['User.Read'],
    };

    this.silentTokenRequest = {
      scopes: ['openid', 'profile', 'User.Read'],
      account: undefined,
      forceRefresh: false,
    };
  }

  /**
   * Calls loginRedirect.
   * @param signInType
   */
  login(): Msal.Account {
    this.msalObj.loginRedirect(this.interactiveLoginRequest);
    return this.msalObj.getAllAccounts()[0];
  }

  /**
   * Gets the token to read user profile data from MS Graph silently, or falls back to interactive popup.
   */
  async getResourceToken(resource?: string): Promise<string | null> {
    this.silentTokenRequest.account = this.msalObj.getAllAccounts()[0];
    if (resource) {
      this.silentTokenRequest.scopes = [resource];
      this.interactiveTokenRequest = { scopes: [resource], account: this.silentTokenRequest.account };
    }

    return this.getTokenRedirect(this.silentTokenRequest, this.interactiveTokenRequest);
  }

  /**
   * Gets a token silently, or falls back to interactive redirect.
   */
  private async getTokenRedirect(
    silentRequest: Msal.AuthenticationParameters,
    interactiveRequest: Msal.AuthenticationParameters
  ): Promise<string | null> {
    try {
      if (__IS_E2E_TESTING__) return Promise.resolve('mockTokenFrom-getTokenPopup');
      const response: Msal.AuthResponse = await this.msalObj.acquireTokenSilent(silentRequest);
      return response.accessToken;
    } catch (e) {
      if (e instanceof Msal.InteractionRequiredAuthError) {
        try {
          if (interactiveRequest.account?.sid) {
            interactiveRequest.account.sid = '';
          }
          this.msalObj.acquireTokenRedirect(interactiveRequest);
          return null;
        } catch (err) {
          console.error(err);
          return null;
        }
      } else {
        console.error(e);
      }
    }
    return null;
  }
}
